// Connect backend eligibility, the pure price calculator, and Alpine price displays.
// Cart and checkout prices remain Shopify's responsibility.
document.addEventListener("alpine:init", () => {
  // Liquid supplies the current store, customer, market, and currency in this JSON.
  const config = JSON.parse(
    document.getElementById("private-sales-context").textContent,
  );
  // Frontend test flag supplied by the private-sales-context JSON in theme.liquid.
  const bypassCache = config.bypassCache === true;
  // Calculations use cents; convert to currency units only when formatting text.
  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: cents % 100 ? 2 : 0,
    }).format(cents / 100);
  const cacheMaxAge = 15 * 60 * 1000;
  Alpine.store("privateSales", {
    // One eligibility snapshot shared by product cards, the PDP, and its sticky price.
    campaigns: [],
    price(product, variant) {
      // Fixed prices/amounts originate in the shop currency. Percentage discounts
      // need no conversion; the calculator applies this rate where appropriate.
      return PrivateSalesPricing.calculate(
        product,
        variant,
        this.campaigns,
        config.currency === config.baseCurrency
          ? 1
          : Number(window.Shopify?.currency?.rate),
      );
    },
    money,
    init() {
      // A single session slot is scoped by the versioned cache key below. Storage
      // may be blocked by the browser; pricing must still work without it.
      let storage;
      try {
        storage = window.sessionStorage;
        if (!config.customerId || bypassCache)
          storage?.removeItem("private-sales");
      } catch {}
      // Do not restore or save session snapshots while bypassing caches.
      if (bypassCache) storage = undefined;
      // Guests keep public prices and do not request personalized campaign data.
      if (!config.customerId) return;
      const options = {
        // Never share personalized results across customers, stores, or markets.
        queryKey: [
          "private-sales",
          config.shop,
          config.customerId,
          config.currency,
          config.country,
        ],
        // Five minutes is freshness, not expiry: old prices remain visible until
        // a successful refresh. Focus/reconnect can refresh stale data; no polling.
        staleTime: bypassCache ? 0 : 5 * 60 * 1000,
        gcTime: cacheMaxAge,
        refetchInterval: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: false,
        queryFn: async ({ signal }) => {
          // The Shopify app proxy signs the logged-in customer's identity. Regios
          // credentials and customer-segment lookups stay on the backend.
          const response = await fetch(
            `${window.Shopify?.routes?.root || "/"}apps/api/private-sales${bypassCache ? "?bypass_cache=1" : ""}`,
            {
              credentials: "same-origin",
              cache: "no-store",
              // Stop on either TanStack cancellation or a ten-second request timeout.
              signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]),
            },
          );
          if (!response.ok) throw new Error("Private prices unavailable");
          const data = await response.json();
          // Reject a response for a different customer instead of displaying it.
          if (
            data.customerId !== String(config.customerId) ||
            !Array.isArray(data.campaigns)
          )
            throw new Error("Invalid pricing context");
          // The backend preserves Regios objects. Build the calculator's compact
          // rules here, once per fetched snapshot, without mutating those objects.
          const result = {
            ...data,
            campaigns: data.campaigns.flatMap((discount) => {
              try {
                return [PrivateSalesPricing.normalizeDiscount(discount)];
              } catch (error) {
                console.warn(
                  "Private-sales display skipped unsupported discount",
                  discount?.id,
                  error.message,
                );
                return [];
              }
            }),
          };
          // Persist only a successful private-sales fetch. Cache reads and focus
          // events never extend its age; storage failures do not affect pricing.
          try {
            storage?.setItem(
              "private-sales",
              JSON.stringify({
                key: cacheKey,
                data: result,
                updatedAt: Date.now(),
              }),
            );
          } catch {}
          return result;
        },
      };
      const cacheKey = JSON.stringify(["v2", ...options.queryKey]);
      const client = window.queryClient;
      client.setQueryDefaults(options.queryKey, { gcTime: cacheMaxAge });
      // Restore before mounting the observer. Preserve the original fetch time
      // so TanStack still refetches after five minutes, including across navigation.
      try {
        const cached = JSON.parse(storage?.getItem("private-sales") ?? "null");
        if (
          cached?.key === cacheKey &&
          Number.isFinite(cached.updatedAt) &&
          cached.updatedAt <= Date.now() &&
          Date.now() - cached.updatedAt < cacheMaxAge &&
          cached.data?.customerId === String(config.customerId) &&
          Array.isArray(cached.data?.campaigns)
        ) {
          client.setQueryData(options.queryKey, cached.data, {
            updatedAt: cached.updatedAt,
          });
        } else {
          storage?.removeItem("private-sales");
        }
      } catch {}
      // Updating this reactive array recalculates every mounted price component.
      // TanStack retains previous data during a refetch or a refresh error.
      const observer = new window.TanstackQuery.QueryObserver(
        window.queryClient,
        options,
      );
      observer.subscribe(({ data }) => {
        this.campaigns = data?.campaigns || [];
      });
      // A fresh restored query may not emit an update when the observer mounts.
      this.campaigns = observer.getCurrentResult().data?.campaigns || [];
    },
  });
  // Product-card adapter. PDP prices use the same store with the selected variant.
  Alpine.data("privateSalePrice", () => ({
    product: null,
    init() {
      this.product = JSON.parse(
        this.$el.querySelector('script[type="application/json"]').textContent,
      );
    },
    get result() {
      if (!this.product) return null;
      // Cards show the cheapest available variant after discounts. If none are
      // available, still show a price using all variants of the sold-out product.
      const variants = this.product.variants.filter((v) => v.available);
      const result = (variants.length ? variants : this.product.variants)
        .map((v) => this.$store.privateSales.price(this.product, v))
        .reduce(
          (best, next) => (!best || next.sale < best.sale ? next : best),
          null,
        );
      // Personalization combines several units of its base variant into one fee.
      const quantity = this.product.quantity ?? 1;
      return (
        result && {
          ...result,
          sale: result.sale * quantity,
          regular: result.regular * quantity,
        }
      );
    },
    get discounted() {
      // Only replace the public-price markup when there is an actual saving.
      return this.result && this.result.sale < this.result.regular;
    },
  }));
});
